import { FieldState, FormState } from 'formstate';
import { useWrapRequest } from 'use-wrap-request';

import {
    ActivityEventType,
    CdnObjectDto,
    CdnObjectType,
    SupportedFitnessApp,
    WorkoutEvent,
    WorkoutRecordingMethod,
    WorkoutTypeV2
} from '@api/FitnessActivityClient';
import { toZonedDateTime, ZonedDateTime } from '@ms-rewards/date-lib';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Api } from '../../../../../core/api';
import { parseGpxSummary } from './gpx';
import {
    positive,
    required,
    useModel,
    wrapInFormField
} from '../../../../../core/forms';
import { injectTSDI } from '../../../../../core/tsdi';
import { UserContext } from '../../../../../users/user';
import { EmployeeContext } from '../../../index';
import { DEFAULT_TIME_ZONE } from '@components/utils/time';

export function useStore(onSuccess?: () => void) {
    const { fitnessActivityClient } = injectTSDI(Api);
    const employee = React.useContext(EmployeeContext);
    const user = React.useContext(UserContext);

    const addWorkoutRequest = useWrapRequest(
        async (data: WorkoutEvent) => {
            const result =
                await fitnessActivityClient.admin.activitySupportControllerCreateWorkoutActivity(
                    data
                );
            if (onSuccess) {
                onSuccess();
            }
            return result;
        },
        { successMessage: () => 'Workout added' }
    );

    const model = useModel(() => {
        const isAfterStart = (
            val: ZonedDateTime | undefined
        ): string | false => {
            if (!val) {
                return 'Start / End dates';
            }
            const fromField = form.$.from;

            if (fromField.value && val.isBefore(fromField.value)) {
                return 'End time must be after start time';
            }

            return false;
        };
        const now = ZonedDateTime.now('Europe/Berlin');
        const form = new FormState({
            workoutType: new FieldState<WorkoutTypeV2>(
                WorkoutTypeV2.CYCLING
            ).validators(required()),
            timeZone: new FieldState<string>(DEFAULT_TIME_ZONE),

            from: new FieldState<ZonedDateTime>(now).validators(required()),
            to: new FieldState<ZonedDateTime>(now).validators(
                required(),
                isAfterStart
            ),
            distance: new FieldState<number>(0).validators(
                required(),
                positive()
            ),
            averageSpeed: new FieldState<number | undefined>(
                undefined
            ).validators(positive()),
            elevation: new FieldState<number | undefined>(undefined).validators(
                positive()
            ),
            caloriesBurned: new FieldState<number | undefined>(
                undefined
            ).validators(positive())
        });

        return {
            form,
            fields: {
                workoutType: wrapInFormField(form.$.workoutType),
                from: wrapInFormField(form.$.from),
                to: wrapInFormField(form.$.to),
                distance: wrapInFormField(form.$.distance),
                timeZone: wrapInFormField(form.$.timeZone),
                averageSpeed: wrapInFormField(form.$.averageSpeed),
                elevation: wrapInFormField(form.$.elevation),
                caloriesBurned: wrapInFormField(form.$.caloriesBurned)
            }
        };
    });

    const [gpxFile, setGpxFile] = React.useState<File | null>(null);
    const [gpxError, setGpxError] = React.useState<string | null>(null);

    const applyGpxFile = async (file: File) => {
        setGpxError(null);
        try {
            const summary = parseGpxSummary(await file.text());
            const { from, to, distance, timeZone, averageSpeed, elevation } =
                model.fields;
            if (summary.from) {
                from.onChange(toZonedDateTime(summary.from, timeZone.value));
            }
            if (summary.to) {
                to.onChange(toZonedDateTime(summary.to, timeZone.value));
            }
            distance.onChange(summary.distanceMeters);
            averageSpeed.onChange(summary.averageSpeedMps);
            elevation.onChange(summary.elevationGainMeters);
            setGpxFile(file);
        } catch (err) {
            setGpxFile(null);
            setGpxError(
                err instanceof Error ? err.message : 'Could not read GPX file'
            );
        }
    };

    const clearGpxFile = () => {
        setGpxFile(null);
        setGpxError(null);
    };

    const uploadGpxFile = async (file: File): Promise<CdnObjectDto> => {
        const result =
            await fitnessActivityClient.admin.cdnObjectSupportControllerUpload(
                { type: CdnObjectType.GPX },
                { file }
            );
        return result.data;
    };

    const onSubmitHandler = async () => {
        const validation = await model.form.validate();
        const {
            workoutType,
            from,
            to,
            distance,
            timeZone,
            averageSpeed,
            elevation,
            caloriesBurned
        } = model.fields;

        const userDay = toZonedDateTime(
            from.value,
            ZonedDateTime.getLocalTZ()
        ).format('yyyy-MM-DD');

        if (validation.hasError) {
            throw new Error(model.form.error as string);
        }
        try {
            const gpx = gpxFile ? await uploadGpxFile(gpxFile) : undefined;
            await addWorkoutRequest.request([
                {
                    messageId: uuidv4().toString(),
                    userId: employee.mySportsId || user.id,
                    timestamp: from.value,
                    userDay: userDay,
                    type: ActivityEventType.WORKOUT,
                    fitnessApp: SupportedFitnessApp.MOVE_REPUBLIC_ADMIN,
                    payload: {
                        rawWorkoutType: workoutType.value.toString(),
                        ...(gpx && {
                            gpx,
                            workoutTypeV2: workoutType.value,
                            recordingMethod: WorkoutRecordingMethod.GPS
                        }),
                        sources: ['com.mysports.support.admin-portal'],
                        devices: [],
                        dateTimeRange: {
                            from: toZonedDateTime(
                                from.value,
                                timeZone.value
                            ).toString(),
                            to: toZonedDateTime(
                                to.value,
                                timeZone.value
                            ).toString()
                        },
                        properties: {
                            distance: distance.value,
                            averageSpeed: averageSpeed.value,
                            elevation: elevation.value,
                            caloriesBurned: caloriesBurned.value,
                            speedEntries: []
                        }
                    }
                }
            ]);
        } catch (err) {
            alert(err);
        }
    };

    return {
        fields: model.fields,
        errors: model.form.error,
        onSubmitHandler,
        addWorkoutRequest: addWorkoutRequest,
        gpxFile,
        gpxError,
        applyGpxFile,
        clearGpxFile
    };
}
